import { useState, useEffect } from "react";
import { createCreditRequest, getEmployees, getTeams, getCreditRequests } from "../api";

const IMPORTANCE_LABELS = {
  1: "Very Low",  2: "Low",      3: "Low-Moderate", 4: "Moderate",
  5: "Normal",    6: "Notable",  7: "High",          8: "Very High",
  9: "Critical",  10: "Blocking",
};

const impColor = (s) =>
  s <= 3 ? "var(--success, #15803d)" : s <= 6 ? "var(--warning, #b45309)" : "var(--danger, #b91c1c)";
const impBg = (s) =>
  s <= 3 ? "var(--success-light, #f0fdf4)" : s <= 6 ? "var(--warning-light, #fef3c7)" : "var(--danger-light, #fee2e2)";

export default function CreditRequestForm({ auth, onSubmitted }) {
  const [employees,   setEmployees]   = useState([]);
  const [teams,       setTeams]       = useState([]);
  const [hasHistory,  setHasHistory]  = useState(false);
  const [checkingDb,  setCheckingDb]  = useState(true);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState("");
  const [success,     setSuccess]     = useState(false);

  const [form, setForm] = useState({
    requester_email:        auth?.email || "",
    requester_name:         auth?.name  || "",
    team:                   "",
    department:             "",
    project_name:           "",
    credits_requested:      "",
    business_justification: "",
    impact_from_previous:   "",
    importance_score:       5,
    manager_email:          "",
  });

  useEffect(() => {
    Promise.all([
      getEmployees().catch(() => []),
      getTeams().catch(() => []),
      auth?.email
        ? getCreditRequests({ requester_email: auth.email }).catch(() => [])
        : Promise.resolve([]),
    ]).then(([emps, tms, prevReqs]) => {
      setEmployees(emps);
      setTeams(tms);
      setHasHistory(prevReqs.length > 0);
      setCheckingDb(false);
    });
  }, [auth?.email]);

  const handleTeamChange = (teamName) => {
    const t = teams.find((t) => t.name === teamName);
    setForm((f) => ({
      ...f,
      team:          teamName,
      department:    t?.department || f.department,
      manager_email: t?.manager_email || f.manager_email,
    }));
  };

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.project_name || !form.credits_requested || !form.business_justification) {
      setError("Project name, credits requested, and business justification are required.");
      return;
    }
    if (hasHistory && !form.impact_from_previous.trim()) {
      setError("Impact from Previous Credits is required since you have prior requests.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await createCreditRequest({
        ...form,
        credits_requested: parseFloat(form.credits_requested),
        importance_score:  parseInt(form.importance_score),
      });
      setSuccess(true);
      if (onSubmitted) onSubmitted();
    } catch (err) {
      setError(err.message || "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div>
        <div className="page-header">
          <h1>Request Claude Credits</h1>
        </div>
        <div className="page-body">
          <div className="card card-body" style={{ maxWidth: 560, textAlign: "center" }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
            <h2 style={{ margin: "0 0 8px", fontSize: 18 }}>Request Submitted</h2>
            <p className="text-muted" style={{ marginBottom: 24 }}>
              Your credit request has been sent to your manager for review. You'll receive an email once a decision is made.
            </p>
            <button className="btn btn-primary" onClick={() => setSuccess(false)}>
              Submit Another Request
            </button>
          </div>
        </div>
      </div>
    );
  }

  const impScore = parseInt(form.importance_score);

  return (
    <div>
      <div className="page-header">
        <h1>Request Claude Credits</h1>
        <p>Submit a request for enterprise Claude credits. Your manager reviews first, then gives final approval.</p>
      </div>

      <div className="page-body">
        <div className="card" style={{ maxWidth: 680 }}>
          <div className="card-body">

            {error && (
              <div className="error-msg" style={{
                background: "var(--danger-light, #fee2e2)",
                border: "1px solid #fca5a5",
                borderRadius: 6,
                padding: "10px 14px",
                marginBottom: 20,
              }}>
                {error}
              </div>
            )}

            {checkingDb ? (
              <div className="text-muted" style={{ marginBottom: 20 }}>Checking your request history…</div>
            ) : null}

            <form onSubmit={handleSubmit}>

              {/* Requester info */}
              <div className="form-row">
                <div className="form-group">
                  <label>Your Name</label>
                  <input value={form.requester_name}
                    onChange={(e) => set("requester_name", e.target.value)} required />
                </div>
                <div className="form-group">
                  <label>Your Email</label>
                  <input type="email" value={form.requester_email}
                    onChange={(e) => set("requester_email", e.target.value)} required />
                </div>
              </div>

              {/* Team & Manager */}
              <div className="form-row">
                <div className="form-group">
                  <label>Team</label>
                  <select value={form.team} onChange={(e) => handleTeamChange(e.target.value)}>
                    <option value="">— Select team —</option>
                    {teams.map((t) => (
                      <option key={t.id} value={t.name}>{t.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Manager Email</label>
                  <input type="email" value={form.manager_email}
                    onChange={(e) => set("manager_email", e.target.value)}
                    placeholder="auto-filled from team" />
                </div>
              </div>

              {/* Project name */}
              <div className="form-group">
                <label>
                  Project / Use-Case Name <span style={{ color: "var(--danger, #ef4444)" }}>*</span>
                </label>
                <input value={form.project_name}
                  onChange={(e) => set("project_name", e.target.value)}
                  placeholder="e.g. Customer support automation, Contract review assistant"
                  required />
              </div>

              {/* Credits */}
              <div className="form-group">
                <label>
                  Credits Requested <span style={{ color: "var(--danger, #ef4444)" }}>*</span>
                  <span className="form-hint" style={{ display: "inline", marginLeft: 8 }}>(Anthropic credit units)</span>
                </label>
                <input type="number" min="1" step="any"
                  value={form.credits_requested}
                  onChange={(e) => set("credits_requested", e.target.value)}
                  placeholder="e.g. 500"
                  required />
              </div>

              {/* Business justification */}
              <div className="form-group">
                <label>
                  Business Justification <span style={{ color: "var(--danger, #ef4444)" }}>*</span>
                </label>
                <textarea rows={4} value={form.business_justification}
                  onChange={(e) => set("business_justification", e.target.value)}
                  placeholder="Describe what you'll use these credits for, expected usage volume, and business impact…"
                  required />
              </div>

              {/* Impact from previous — only shown when user has prior requests */}
              {!checkingDb && hasHistory && (
                <div className="form-group">
                  <label>
                    Impact from Previous Credits <span style={{ color: "var(--danger, #ef4444)" }}>*</span>
                    <span className="form-hint" style={{ display: "inline", marginLeft: 8 }}>required — you have prior requests</span>
                  </label>
                  <textarea rows={3} value={form.impact_from_previous}
                    onChange={(e) => set("impact_from_previous", e.target.value)}
                    placeholder="e.g. Reduced manual review time by 40%, processed 12K documents last quarter…"
                    required />
                </div>
              )}

              {/* Importance score */}
              <div className="form-group">
                <label style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span>Importance Score</span>
                  <span className="badge" style={{
                    background: impBg(impScore),
                    color: impColor(impScore),
                  }}>
                    {impScore} / 10 — {IMPORTANCE_LABELS[impScore]}
                  </span>
                </label>
                <input type="range" min="1" max="10" step="1"
                  value={form.importance_score}
                  onChange={(e) => set("importance_score", e.target.value)}
                  style={{ width: "100%", accentColor: impColor(impScore), cursor: "pointer" }} />
                <div style={{ display: "flex", justifyContent: "space-between" }} className="form-hint">
                  <span>1 — Low priority</span>
                  <span>5 — Normal</span>
                  <span>10 — Blocking</span>
                </div>
              </div>

              <button type="submit" className="btn btn-primary" disabled={loading || checkingDb}
                style={{ minWidth: 160 }}>
                {loading ? "Submitting…" : "Submit Request"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
